import {
  CHART_COLORS,
  resolveChartColor,
  UPLOT_TICK_FONT,
  UPLOT_TITLE_FONT,
} from 'features/timeSeries/config/constants'
import { getSensorColor } from 'features/timeSeries/config/timeSeriesColors'
import {
  resolveSingleScaleYAxisRange,
  resolveYAxisRange,
  SensorRangeMap,
} from 'features/timeSeries/utils/axisRangeHelpers'
import { consumeProgrammaticXRange } from 'features/timeSeries/utils/chartViewportSignal'
import type { ScaleRanges } from 'features/timeSeries/utils/chartViewportTransition'
import {
  EnhancedSensorEntry,
  SensorAxisMapping,
  TimeSeriesDerivedValueEntryJSON,
} from 'types'
import uPlot from 'uplot'

const X_AXIS_SPACE = 60
const AXIS_GAP = 2
const TICK_SIZE = 5
/** Matches DRAG_THRESHOLD_PX in useChartViewportInteractions' custom box zoom. */
const DRAG_MIN_DIST_PX = 4

interface UPlotConfigOptions {
  sensorAxisMapping: SensorAxisMapping
  singleScaleMode: boolean
  allSensorsToRender: Array<{ id: string; name: string }>
  enhancedSensorEntries: EnhancedSensorEntry[]
  customAxisRanges: Record<string, { min?: number; max?: number }>
  sensorRanges: SensorRangeMap
  derivedValues: TimeSeriesDerivedValueEntryJSON[]
  xAxisSensorId?: string
  customXAxisRange?: { min?: number; max?: number }
  unitMapping: Record<string, string>
  customSensorColors?: Record<string, string>
  width: number
  height: number
  onZoom?: (min: number, max: number) => void
  plugins?: uPlot.Plugin[]
}

/**
 * The pinned numeric limits of a scale set, in the form `useUPlotChart` applies
 * them to the live plot with `setScale`.
 *
 * This is the counterpart to `buildUPlotOptionsStructuralSignature`, which
 * deliberately drops these numbers so that a new window does not read as a new
 * chart and force a teardown. They are applied, not discarded — this is where
 * they are read back out of the config produced by `generateUPlotScales`.
 *
 * Only fully-pinned scales appear: a range with an `auto` end (or no range at
 * all) is uPlot's to compute, and asserting a limit onto it would override the
 * autoscaling rather than follow it.
 */
export function buildUPlotScaleRanges(scales: uPlot.Scales): ScaleRanges {
  const ranges: ScaleRanges = {}

  Object.entries(scales).forEach(([scaleKey, scale]) => {
    const range = scale.range

    if (!Array.isArray(range) || range.length !== 2) return

    const [min, max] = range
    if (typeof min !== 'number' || typeof max !== 'number') return
    if (!Number.isFinite(min) || !Number.isFinite(max)) return

    ranges[scaleKey] = { min, max }
  })

  return ranges
}

export function generateUPlotScales(options: UPlotConfigOptions): uPlot.Scales {
  const {
    xAxisSensorId,
    singleScaleMode,
    allSensorsToRender,
    sensorRanges,
    customAxisRanges,
    customXAxisRange,
  } = options

  const scales: uPlot.Scales = {}

  if (xAxisSensorId) {
    scales.x = {
      time: false,
      auto: true,
      ...(customXAxisRange &&
        (customXAxisRange.min !== undefined ||
          customXAxisRange.max !== undefined) && {
          auto: false,
          range: [
            customXAxisRange.min ?? null,
            customXAxisRange.max ?? null,
          ] as uPlot.Range.MinMax,
        }),
    }
  } else {
    scales.x = {
      time: true,
      auto: true,
      ...(customXAxisRange &&
        (customXAxisRange.min !== undefined ||
          customXAxisRange.max !== undefined) && {
          auto: false,
          range: [
            customXAxisRange.min ?? null,
            customXAxisRange.max ?? null,
          ] as uPlot.Range.MinMax,
        }),
    }
  }

  if (singleScaleMode) {
    const range = resolveSingleScaleYAxisRange(
      allSensorsToRender,
      sensorRanges,
      customAxisRanges['single-scale']
    )
    scales.y = { auto: false, range: [range.min, range.max] }
  } else {
    allSensorsToRender.forEach(({ id: sensorId }) => {
      const range = resolveYAxisRange(
        sensorRanges[sensorId],
        customAxisRanges[sensorId]
      )
      scales[sensorId] = { auto: false, range: [range.min, range.max] }
    })
  }

  return scales
}

export function generateUPlotAxes(options: UPlotConfigOptions): uPlot.Axis[] {
  const {
    singleScaleMode,
    allSensorsToRender,
    sensorAxisMapping,
    enhancedSensorEntries,
    derivedValues,
    customSensorColors,
    unitMapping,
    xAxisSensorId,
  } = options

  const axes: uPlot.Axis[] = []

  // Canvas cannot paint `var(--token)` — resolve once per axes build.
  const axisStroke = resolveChartColor(CHART_COLORS.AXIS_STROKE)
  const gridLine = resolveChartColor(CHART_COLORS.GRID_LINE)
  const tickStroke = resolveChartColor(CHART_COLORS.TICK_STROKE)

  // X-axis
  if (xAxisSensorId) {
    const isDerived = xAxisSensorId.startsWith('derived_')
    let xAxisTitle: string
    let xAxisUnit = ''

    if (isDerived) {
      const derivedValue = derivedValues.find((dv) => dv.id === xAxisSensorId)
      xAxisTitle = derivedValue?.data.name || xAxisSensorId
      xAxisUnit = derivedValue?.data.unit || 'No Unit'
    } else {
      const sensor = enhancedSensorEntries.find((e) => e.id === xAxisSensorId)
      xAxisTitle = (sensor?.data?.name as string) || xAxisSensorId
      const unit = unitMapping[xAxisTitle]
      xAxisUnit = unit && unit !== 'No Unit' ? ` (${unit})` : ''
    }

    axes.push({
      scale: 'x',
      show: true,
      side: 2,
      label: `${xAxisTitle}${xAxisUnit}`,
      labelFont: UPLOT_TITLE_FONT,
      font: UPLOT_TICK_FONT,
      stroke: axisStroke,
      grid: { show: true, stroke: gridLine, width: 1 },
      ticks: {
        show: true,
        stroke: tickStroke,
        width: 1,
        size: TICK_SIZE,
      },
      space: X_AXIS_SPACE,
      gap: AXIS_GAP,
    })
  } else {
    axes.push({
      scale: 'x',
      show: true,
      side: 2,
      font: UPLOT_TICK_FONT,
      labelFont: UPLOT_TITLE_FONT,
      stroke: axisStroke,
      grid: { show: true, stroke: gridLine, width: 1 },
      ticks: {
        show: true,
        stroke: tickStroke,
        width: 1,
        size: TICK_SIZE,
      },
      space: X_AXIS_SPACE,
      gap: AXIS_GAP,
    })
  }

  // Y-axes -- tick labels hidden; grid lines preserved; overlay labels show Ymin/Ymax
  const hiddenYAxisValues = () => ['']

  if (singleScaleMode) {
    axes.push({
      scale: 'y',
      show: allSensorsToRender.length > 0,
      side: 3,
      font: UPLOT_TICK_FONT,
      stroke: axisStroke,
      grid: { show: true, stroke: gridLine, width: 1 },
      ticks: { show: false },
      values: hiddenYAxisValues,
      size: 0,
      gap: 0,
    })
  } else {
    allSensorsToRender.forEach(({ id: sensorId }, sensorIndex) => {
      const axisInfo = sensorAxisMapping[sensorId]
      if (!axisInfo) return

      const axisColor =
        customSensorColors?.[sensorId] || getSensorColor(sensorIndex)

      const side: 1 | 3 = axisInfo.side === 'right' ? 1 : 3

      const showGrid = sensorIndex === 0

      axes.push({
        scale: sensorId,
        show: true,
        side,
        font: UPLOT_TICK_FONT,
        stroke: axisColor,
        grid: showGrid
          ? { show: true, stroke: gridLine, width: 1 }
          : { show: false },
        ticks: { show: false },
        values: hiddenYAxisValues,
        size: 0,
        gap: 0,
      })
    })
  }

  return axes
}

export function generateUPlotOptions(
  options: UPlotConfigOptions
): uPlot.Options {
  const { width, height, onZoom, plugins = [], xAxisSensorId } = options

  const scales = generateUPlotScales(options)
  const axes = generateUPlotAxes(options)

  return {
    width,
    height,
    ms: xAxisSensorId ? undefined : 1,
    scales,
    axes,
    legend: { show: false },
    cursor: {
      show: true,
      x: true,
      y: false,
      drag: {
        x: true,
        y: false,
        setScale: true,
        // uPlot defaults `dist` to 0, which commits a zoom on a 1-pixel drag --
        // i.e. an ordinary click can blank the chart. Require a deliberate drag.
        dist: DRAG_MIN_DIST_PX,
      },
      points: {
        show: true,
        size: 8,
        fill: resolveChartColor(CHART_COLORS.CURSOR_ACCENT),
        stroke: resolveChartColor(CHART_COLORS.BACKGROUND),
        width: 2,
      },
    },
    series: [{}],
    hooks: onZoom
      ? (() => {
          // Track the last known x-range so we only fire onZoom when the user
          // actually changes it (drag-to-zoom), not on the scale sets that
          // happen during uPlot construction or that we push ourselves.
          //
          // This closure belongs to the OPTIONS object, and a retained options
          // object can serve several uPlot instances in sequence (the create
          // effect recreates on `hasDataPoints` flips without regenerating
          // options). The baseline is therefore managed per instance:
          //
          //  - `init` resets it, so a new instance never compares its first
          //    scale-set against the PREVIOUS instance's window — that stale
          //    comparison read construction as a user zoom and committed a
          //    phantom range change (exiting live tail with it).
          //  - `ready` primes it from the instance's actual x scale if nothing
          //    else has: uPlot only fires setScale('x') when the range CHANGES
          //    during the first commit, so a mount path that comes up already
          //    at its range would otherwise leave the baseline null and the
          //    null-branch below would swallow the user's first drag.
          let lastXMin: number | null = null
          let lastXMax: number | null = null
          return {
            init: [
              () => {
                lastXMin = null
                lastXMax = null
              },
            ],
            ready: [
              (u: uPlot) => {
                if (lastXMin !== null) return
                const { min, max } = u.scales.x
                if (typeof min === 'number' && typeof max === 'number') {
                  lastXMin = min
                  lastXMax = max
                }
              },
            ],
            setScale: [
              (u: uPlot, scaleKey: string) => {
                if (scaleKey !== 'x') return
                const xMin = u.scales.x.min
                const xMax = u.scales.x.max
                if (
                  xMin === null ||
                  xMin === undefined ||
                  xMax === null ||
                  xMax === undefined
                )
                  return

                // We pushed this scale, not the user. Still record the range:
                // the next genuine zoom must be compared against where the plot
                // actually is, or it reads as a change that already happened.
                // Without this the transition reported every frame as a zoom and
                // re-fetched itself ~20 times a second.
                if (consumeProgrammaticXRange(xMin, xMax)) {
                  lastXMin = xMin
                  lastXMax = xMax
                  return
                }

                if (lastXMin === null) {
                  lastXMin = xMin
                  lastXMax = xMax
                  return
                }

                if (xMin === lastXMin && xMax === lastXMax) return

                lastXMin = xMin
                lastXMax = xMax
                onZoom(xMin, xMax)
              },
            ],
          }
        })()
      : {},
    plugins,
  }
}
